/**
 * Brand CTA colors: parse theme hex/rgb, build gradient CSS variables,
 * optional average color from logo or background bitmap.
 */

export function parseCssColorToHex(input) {
  if (input == null || typeof input !== "string") return null;
  const s = input.trim();
  if (!s) return null;
  const hex6 = /^#([0-9a-f]{6})$/i.exec(s);
  if (hex6) return `#${hex6[1].toLowerCase()}`;
  const hex3 = /^#([0-9a-f]{3})$/i.exec(s);
  if (hex3) {
    const [a, b, c] = hex3[1].toLowerCase().split("");
    return `#${a}${a}${b}${b}${c}${c}`;
  }
  const rgb = /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i.exec(s);
  if (rgb) {
    const r = clamp255(rgb[1]);
    const g = clamp255(rgb[2]);
    const b = clamp255(rgb[3]);
    return `#${toHex2(r)}${toHex2(g)}${toHex2(b)}`;
  }
  return null;
}

function clamp255(n) {
  const x = Number(n);
  if (Number.isNaN(x)) return 0;
  return Math.max(0, Math.min(255, Math.round(x)));
}

function toHex2(n) {
  return clamp255(n).toString(16).padStart(2, "0");
}

function hexToRgb(hex) {
  const h = parseCssColorToHex(hex);
  if (!h) return null;
  const n = parseInt(h.slice(1), 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

function mix255(c, target, t) {
  return clamp255(Math.round(c + (target - c) * t));
}

export function lightenHex(hex, t) {
  const rgb = hexToRgb(hex);
  if (!rgb) return "#eab308";
  return `#${toHex2(mix255(rgb.r, 255, t))}${toHex2(mix255(rgb.g, 255, t))}${toHex2(
    mix255(rgb.b, 255, t)
  )}`;
}

export function darkenHex(hex, t) {
  const rgb = hexToRgb(hex);
  if (!rgb) return "#a16207";
  return `#${toHex2(mix255(rgb.r, 0, t))}${toHex2(mix255(rgb.g, 0, t))}${toHex2(
    mix255(rgb.b, 0, t)
  )}`;
}

export function hexToRgba(hex, a) {
  const rgb = hexToRgb(hex);
  if (!rgb) return `rgba(234, 179, 8, ${a})`;
  return `rgba(${rgb.r},${rgb.g},${rgb.b},${a})`;
}

/**
 * CSS custom properties for .begin-play-btn--themed
 * @param {string} accentHex
 * @param {{ textHex?: string | null }} [opts]
 */
export function buildCtaCssProps(accentHex, opts = {}) {
  const base = parseCssColorToHex(accentHex) || "#eab308";
  const text = opts.textHex ? parseCssColorToHex(opts.textHex) : null;
  const props = {
    "--cta-a": lightenHex(base, 0.2),
    "--cta-b": base,
    "--cta-c": darkenHex(base, 0.16),
    "--cta-d": darkenHex(base, 0.3),
    "--cta-glow": hexToRgba(base, 0.52),
    "--cta-glow-soft": hexToRgba(base, 0.32),
    "--cta-rim": darkenHex(base, 0.36),
    "--cta-rim-hover": darkenHex(base, 0.44),
  };
  if (text) props["--cta-text"] = text;
  return props;
}

function imageNeedsCrossOrigin(src) {
  if (typeof window === "undefined" || !src) return false;
  try {
    const u = new URL(src, window.location.href);
    return u.origin !== window.location.origin;
  } catch {
    return false;
  }
}

/**
 * Average opaque pixel color from a downsized draw (logo / bg).
 * @param {string} src
 * @param {(hex: string | null) => void} done
 */
export function sampleImageAverageHex(src, done) {
  if (!src || typeof src !== "string") {
    done(null);
    return;
  }
  const img = new Image();
  if (imageNeedsCrossOrigin(src)) {
    img.crossOrigin = "anonymous";
  }
  img.onload = () => {
    try {
      const max = 48;
      const nw = img.naturalWidth || img.width;
      const nh = img.naturalHeight || img.height;
      if (!nw || !nh) {
        done(null);
        return;
      }
      const w = Math.min(max, nw);
      const h = Math.min(max, nh);
      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d", { willReadFrequently: true });
      if (!ctx) {
        done(null);
        return;
      }
      ctx.drawImage(img, 0, 0, w, h);
      const { data } = ctx.getImageData(0, 0, w, h);
      let r = 0;
      let g = 0;
      let b = 0;
      let n = 0;
      for (let i = 0; i < data.length; i += 4) {
        if (data[i + 3] < 20) continue;
        r += data[i];
        g += data[i + 1];
        b += data[i + 2];
        n++;
      }
      if (!n) {
        done(null);
        return;
      }
      r = Math.round(r / n);
      g = Math.round(g / n);
      b = Math.round(b / n);
      done(`#${toHex2(r)}${toHex2(g)}${toHex2(b)}`);
    } catch {
      done(null);
    }
  };
  img.onerror = () => done(null);
  img.src = src;
}

export function pickBackgroundSampleUrl(userData) {
  if (!userData || typeof userData !== "object") return "";
  const desk =
    typeof userData.background_desk === "string" ? userData.background_desk.trim() : "";
  const mob =
    typeof userData.background_mob === "string" ? userData.background_mob.trim() : "";
  if (typeof window !== "undefined" && window.innerWidth <= 768) {
    return mob || desk || "";
  }
  return desk || mob || "";
}
